/**
 * Builds an origin for a target multi-community site based on the current site's origin.
 *
 * Handles both:
 * - subdomain differentiation (e.g., austin.eater.com)
 * - domain switching (e.g., sbnation.com -> mmafighting.com)
 *
 * @param origin - The current site's origin (e.g., 'https://www.eater.com' or 'https://austin.eater.com')
 * @param targetSubdomain - The target subdomain.
 * - Use null for primary community
 * - Or a subdomain (e.g., 'austin', 'liverpooloffside')
 * @param targetDomain - The target domain.
 * - Use null to keep current domain
 * - Or specify a new domain (e.g., 'mmafighting.com')
 * - Or specify a new domain with a subdomain (e.g., 'austin.eater.com')
 *   - Note subdomain community `domain` is stored as `subdomain.second-level-domain.com`
 *   - See packages/duet-framework/src/config/community/communities.ts
 *   - This will work without subdomain in tagetDomain if you aren't using data from communities.ts
 * @returns The formatted origin for the target community
 */
export const getMultiCommunityOrigin = ({
  origin,
  targetSubdomain = null,
  targetDomain = null,
}: {
  origin: string;
  targetDomain?: string | null;
  targetSubdomain?: string | null;
}): string => {
  const url = new URL(origin);
  const { hostname } = url;
  const hostParts = hostname.split(".");

  if (!hostParts.length) {
    throw new Error(`Host parts not found in hostname ${hostname}`);
  }

  const voxopsTopLevelDomain = "duet.voxops.net";
  const isVoxops = hostname.includes(voxopsTopLevelDomain);

  // Determine the second level domain (e.g., 'eater' or 'sbnation')
  const currentSecondLevelDomain = isVoxops
    ? hostParts.at(-4) // Get part before 'duet.voxops.net'
    : hostParts.at(-2); // Get part before 'com'

  if (!currentSecondLevelDomain) {
    throw new Error(`Domain not found in hostname ${hostname}`);
  }

  // If there is a target domain abstract the second level domain from it
  const secondLevelDomain = targetDomain
    ? targetDomain.split(".").at(-2)
    : currentSecondLevelDomain;

  // Handle dev environment
  if (hostParts.includes("dev")) {
    const port = url.port ? `:${url.port}` : "";
    return targetSubdomain
      ? `http://dev.${targetSubdomain}.${secondLevelDomain}.com${port}`
      : `http://dev.${secondLevelDomain}.com${port}`;
  }

  /**
   * Get subdomains for the current origin:
   * - If it's a Voxops site, everything before the last 4 parts ('<domain>.duet.voxops.net')
   * - If it's not a Voxops site, everything before the last 2 parts ('<domain>.com')
   */
  const currentSubdomains = hostParts.slice(0, isVoxops ? -4 : -2);

  /**
   * If subdomains is 2 parts its a subdomain subcommunity, if its 1 its either the primary
   * community or a non-subdomain subcommunity.
   */
  const currentIsSubdomainSubcommunity = currentSubdomains.length === 2;

  /**
   * Handle production `www` Fastly proxy cases (e.g. www.eater.com or austin.eater.com).
   * These are always three parts regardless of being a primary community, subdomain
   * sub-community, or non-subdomain sub-community. If there is a target subdomain use it,
   * otherwise use www.
   */
  if (
    hostParts.length === 3 &&
    // Duet Fastly proxy subdomain is `www`
    process.env.NEXT_PUBLIC_DUET_PROXY_SUBDOMAIN === "www"
  ) {
    return `https://${targetSubdomain || "www"}.${secondLevelDomain}.com`;
  }

  /**
   * If we have a targetSubdomain:
   * - If we're on a subdomain sub-community, replace the subdomain with the targetSubdomain
   * - Otherwise, add the targetSubdomain to the start of the subdomains array
   *
   * If we don't have a targetSubdomain:
   * - If we're on a subdomain sub-community, remove the subdomain
   * - Otherwise, leave the subdomains array as is (should just be environment related subdomains)
   */
  const newSubdomains = targetSubdomain
    ? [
        targetSubdomain,
        // If we're on a subdomain sub-community, remove subdomain (replaced by targetSubdomain)
        ...currentSubdomains.slice(currentIsSubdomainSubcommunity ? 1 : 0),
      ]
    : currentIsSubdomainSubcommunity
      ? // If we're on a subdomain sub-community, remove subdomain (target has no community subdomain)
        currentSubdomains.slice(1)
      : currentSubdomains;

  return `https://${newSubdomains.join(".")}.${secondLevelDomain}.${isVoxops ? voxopsTopLevelDomain : "com"}`;
};
